import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Search, Truck, Route } from "lucide-react";

export default function AseoUrbanoPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <div className="space-y-6" data-testid="aseo-urbano-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3"><Truck className="w-8 h-8 text-orange-500" />Aseo Urbano</h1>
        <p className="text-green-700/70 mt-1">Tabulador de tarifas y rutas de recolección</p>
      </div>

      <Tabs defaultValue="tarifas" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tarifas" data-testid="tab-tarifas"><Truck className="w-4 h-4 mr-1.5" />Tarifas</TabsTrigger>
          <TabsTrigger value="rutas" data-testid="tab-rutas"><Route className="w-4 h-4 mr-1.5" />Rutas</TabsTrigger>
        </TabsList>
        <TabsContent value="tarifas"><Tarifas auth={auth} API={API} /></TabsContent>
        <TabsContent value="rutas"><Rutas auth={auth} API={API} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Tarifas({ auth, API }) {
  const [clas, setClas] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCod, setFiltroCod] = useState("ALL");
  const [tipoTab, setTipoTab] = useState("no-industrial");  // "no-industrial" | "industrial"
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    codigo_clasificacion:"", actividad_economica:"", actividad_especifica:"",
    tipo: "no-industrial",
    // No industrial
    tarifa_hasta_150:0, tarifa_151_300:0, tarifa_mas_300:0,
    // Industrial
    tarifa_hasta_50:"", tarifa_51_100:"", tarifa_101_400:"",
    // Compartidos (industrial)
    tarifa_401_700:"", tarifa_701_1000:"", tarifa_mas_1000:"",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [c, t] = await Promise.all([
      axios.get(`${API}/aseo-urbano/clasificaciones`, auth),
      axios.get(`${API}/aseo-urbano/tarifas`, { ...auth, params: { codigo_clasificacion: filtroCod === "ALL" ? "" : filtroCod, busqueda } }),
    ]);
    setClas(c.data); setTarifas(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroCod, busqueda]);

  // Filtro por tipo (industrial | no-industrial) — retrocompat: tarifas viejas sin tipo se asumen no-industrial
  const [colFilters, setColFilters] = useState({});  // { clasif, actecon, actesp, t50, t51_100, t101_400, t401_700, t701_1000, tmas_1000, t150, t151_300, tmas_300 }
  const setCol = (k, v) => setColFilters(prev => ({ ...prev, [k]: v }));
  const clearFilters = () => setColFilters({});
  const hasFilters = Object.values(colFilters).some(v => v && String(v).trim());

  const matchText = (row, key, needle) => {
    if (!needle) return true;
    return String(row?.[key] ?? "").toLowerCase().includes(String(needle).toLowerCase());
  };
  const matchNum = (row, key, needle) => {
    if (!needle) return true;
    return String(row?.[key] ?? "").includes(String(needle).trim());
  };

  const tarifasFiltradas = tarifas
    .filter(t => (t.tipo || "no-industrial") === tipoTab)
    .filter(t => matchText(t, "codigo_clasificacion", colFilters.clasif))
    .filter(t => matchText(t, "actividad_economica",  colFilters.actecon))
    .filter(t => matchText(t, "actividad_especifica", colFilters.actesp))
    .filter(t => tipoTab === "no-industrial"
      ? (matchNum(t, "tarifa_hasta_150", colFilters.t150) &&
         matchNum(t, "tarifa_151_300",   colFilters.t151_300) &&
         matchNum(t, "tarifa_mas_300",   colFilters.tmas_300))
      : (matchNum(t, "tarifa_hasta_50",   colFilters.t50) &&
         matchNum(t, "tarifa_51_100",    colFilters.t51_100) &&
         matchNum(t, "tarifa_101_400",   colFilters.t101_400) &&
         matchNum(t, "tarifa_401_700",   colFilters.t401_700) &&
         matchNum(t, "tarifa_701_1000",  colFilters.t701_1000) &&
         matchNum(t, "tarifa_mas_1000",  colFilters.tmas_1000)));

  const countNoInd = tarifas.filter(t => (t.tipo || "no-industrial") === "no-industrial").length;
  const countInd = tarifas.filter(t => t.tipo === "industrial").length;

  const save = async () => {
    try {
      const optNum = v => (v === "" || v == null ? null : Number(v));
      const isInd = form.tipo === "industrial";
      const payload = { ...form,
        tipo: form.tipo,
        // Solo enviar los campos del tipo seleccionado; los del otro tipo van a null
        tarifa_hasta_150: isInd ? null : Number(form.tarifa_hasta_150) || 0,
        tarifa_151_300:   isInd ? null : Number(form.tarifa_151_300) || 0,
        tarifa_mas_300:   isInd ? null : Number(form.tarifa_mas_300) || 0,
        tarifa_hasta_50:  isInd ? optNum(form.tarifa_hasta_50) : null,
        tarifa_51_100:    isInd ? optNum(form.tarifa_51_100) : null,
        tarifa_101_400:   isInd ? optNum(form.tarifa_101_400) : null,
        tarifa_401_700:   isInd ? optNum(form.tarifa_401_700) : null,
        tarifa_701_1000:  isInd ? optNum(form.tarifa_701_1000) : null,
        tarifa_mas_1000:  isInd ? optNum(form.tarifa_mas_1000) : null,
        tarifa_301_400: null,  // Legacy — ya no se usa
      };
      if (editing) await axios.put(`${API}/aseo-urbano/tarifas/${editing.id}`, payload, auth);
      else await axios.post(`${API}/aseo-urbano/tarifas`, payload, auth);
      toast.success(editing ? "Tarifa actualizada" : "Tarifa creada");
      setOpen(false); setEditing(null); setForm({ ...empty, tipo: tipoTab });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const edit = (t) => {
    setEditing(t);
    setForm({
      ...empty, ...t,
      tipo: t.tipo || "no-industrial",
      tarifa_hasta_50:  t.tarifa_hasta_50  ?? "",
      tarifa_51_100:    t.tarifa_51_100    ?? "",
      tarifa_101_400:   t.tarifa_101_400   ?? "",
      tarifa_401_700:   t.tarifa_401_700   ?? "",
      tarifa_701_1000:  t.tarifa_701_1000  ?? "",
      tarifa_mas_1000:  t.tarifa_mas_1000  ?? "",
      tarifa_hasta_150: t.tarifa_hasta_150 ?? 0,
      tarifa_151_300:   t.tarifa_151_300   ?? 0,
      tarifa_mas_300:   t.tarifa_mas_300   ?? 0,
    });
    setOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, tipo: tipoTab });
    setOpen(true);
  };

  const del = async (t) => { if (!window.confirm("¿Eliminar tarifa?")) return; await axios.delete(`${API}/aseo-urbano/tarifas/${t.id}`, auth); toast.success("Eliminada"); load(); };

  const isInd = form.tipo === "industrial";

  return (
    <div className="space-y-4">
      <Tabs value={tipoTab} onValueChange={setTipoTab}>
        <TabsList>
          <TabsTrigger value="no-industrial" data-testid="tab-tarifas-no-industrial">
            Tarifas No Industriales
            <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 hover:bg-green-100" data-testid="count-no-industrial">{countNoInd}</Badge>
          </TabsTrigger>
          <TabsTrigger value="industrial" data-testid="tab-tarifas-industrial">
            Tarifas Industriales
            <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-800 hover:bg-orange-100" data-testid="count-industrial">{countInd}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex items-center justify-between">
        <div className="text-sm text-green-800 bg-green-50 rounded-lg px-3 py-1.5 border border-green-100" data-testid="tarifas-total">
          Mostrando <span className="font-bold">{tarifasFiltradas.length}</span> {tipoTab === "industrial" ? "industrial(es)" : "no industrial(es)"}
          {hasFilters && <span className="text-orange-600 ml-1">(filtrado)</span>}
          <span className="text-green-600 mx-1.5">·</span>
          Total catálogo: <span className="font-bold">{countNoInd + countInd}</span>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ ...empty, tipo: tipoTab }); } }}>
          <Button data-testid="btn-add-tarifa" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nueva Tarifa {tipoTab === "industrial" ? "Industrial" : "No Industrial"}</Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Editar tarifa" : "Nueva tarifa"} — {isInd ? "Industrial" : "No Industrial"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Tipo de tabulador*</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo:v})}>
                  <SelectTrigger data-testid="input-tarifa-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-industrial">No Industrial (3 tramos)</SelectItem>
                    <SelectItem value="industrial">Industrial (6 tramos)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Clasificación*</Label>
                <Select value={form.codigo_clasificacion} onValueChange={v => setForm({...form, codigo_clasificacion:v})}>
                  <SelectTrigger data-testid="input-tarifa-clasif"><SelectValue placeholder="Seleccionar clasificación" /></SelectTrigger>
                  <SelectContent>{clas.map(c => <SelectItem key={c.codigo} value={c.codigo}><span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded" style={{ background: c.color }} />{c.codigo} - {c.nombre}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Actividad Económica*</Label><Input data-testid="input-tarifa-actecon" value={form.actividad_economica} onChange={e => setForm({...form, actividad_economica:e.target.value})} /></div>
              <div><Label>Actividad Específica*</Label><Input data-testid="input-tarifa-actesp" value={form.actividad_especifica} onChange={e => setForm({...form, actividad_especifica:e.target.value})} /></div>

              {!isInd && <>
                <div><Label>Tarifa ≤ 150 m²</Label><Input type="number" data-testid="input-tarifa-150" value={form.tarifa_hasta_150} onChange={e => setForm({...form, tarifa_hasta_150:e.target.value})} /></div>
                <div><Label>Tarifa 151–300 m²</Label><Input type="number" value={form.tarifa_151_300} onChange={e => setForm({...form, tarifa_151_300:e.target.value})} /></div>
                <div><Label>Tarifa &gt; 300 m²</Label><Input type="number" value={form.tarifa_mas_300} onChange={e => setForm({...form, tarifa_mas_300:e.target.value})} /></div>
              </>}

              {isInd && <>
                <div><Label>Tarifa ≤ 50 m²</Label><Input type="number" data-testid="input-tarifa-50" value={form.tarifa_hasta_50} onChange={e => setForm({...form, tarifa_hasta_50:e.target.value})} placeholder="Bs." /></div>
                <div><Label>Tarifa 51–100 m²</Label><Input type="number" data-testid="input-tarifa-51-100" value={form.tarifa_51_100} onChange={e => setForm({...form, tarifa_51_100:e.target.value})} placeholder="Bs." /></div>
                <div><Label>Tarifa 101–400 m²</Label><Input type="number" data-testid="input-tarifa-101-400" value={form.tarifa_101_400} onChange={e => setForm({...form, tarifa_101_400:e.target.value})} placeholder="Bs." /></div>
                <div><Label>Tarifa 401–700 m²</Label><Input type="number" data-testid="input-tarifa-401-700" value={form.tarifa_401_700} onChange={e => setForm({...form, tarifa_401_700:e.target.value})} placeholder="Bs." /></div>
                <div><Label>Tarifa 701–1000 m²</Label><Input type="number" data-testid="input-tarifa-701-1000" value={form.tarifa_701_1000} onChange={e => setForm({...form, tarifa_701_1000:e.target.value})} placeholder="Bs." /></div>
                <div><Label>Tarifa &gt; 1000 m²</Label><Input type="number" data-testid="input-tarifa-mas-1000" value={form.tarifa_mas_1000} onChange={e => setForm({...form, tarifa_mas_1000:e.target.value})} placeholder="Bs." /></div>
              </>}
            </div>
            <DialogFooter><Button data-testid="btn-save-tarifa" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editing ? "Actualizar" : "Crear"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-green-100">
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="md:col-span-2 flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 focus-within:bg-green-100 transition">
            <Search className="w-4 h-4 text-green-700 pointer-events-none" />
            <Input
              data-testid="input-search-tarifa"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar por actividad…"
              className="bg-transparent border-none focus-visible:ring-0 h-9 text-sm flex-1 min-w-0"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="search"
            />
          </div>
          <Select value={filtroCod} onValueChange={setFiltroCod}>
            <SelectTrigger data-testid="filter-clasificacion"><SelectValue placeholder="Filtrar por clasificación" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas las clasificaciones</SelectItem>
              {clas.map(c => <SelectItem key={c.codigo} value={c.codigo}>{c.codigo} - {c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              {tipoTab === "no-industrial" ? (
                <>
                  <tr>
                    <th className="text-left p-2.5">Clasif.</th>
                    <th className="text-left p-2.5">Actividad económica</th>
                    <th className="text-left p-2.5">Actividad específica</th>
                    <th className="text-right p-2.5">≤150 m²</th>
                    <th className="text-right p-2.5">151-300 m²</th>
                    <th className="text-right p-2.5">&gt;300 m²</th>
                    <th className="text-right p-2.5">
                      {hasFilters && <Button size="sm" variant="ghost" onClick={clearFilters} className="text-xs h-7 px-2 text-red-600 hover:text-red-700" data-testid="btn-clear-filters">Limpiar</Button>}
                    </th>
                  </tr>
                  <tr className="bg-white">
                    <th className="p-1.5"><Input data-testid="colfilter-clasif" value={colFilters.clasif || ""} onChange={e => setCol("clasif", e.target.value)} placeholder="1.1…" className="h-7 text-xs font-mono" /></th>
                    <th className="p-1.5"><Input data-testid="colfilter-actecon" value={colFilters.actecon || ""} onChange={e => setCol("actecon", e.target.value)} placeholder="Filtrar…" className="h-7 text-xs" /></th>
                    <th className="p-1.5"><Input data-testid="colfilter-actesp" value={colFilters.actesp || ""} onChange={e => setCol("actesp", e.target.value)} placeholder="Filtrar…" className="h-7 text-xs" /></th>
                    <th className="p-1.5"><Input data-testid="colfilter-t150" value={colFilters.t150 || ""} onChange={e => setCol("t150", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.t151_300 || ""} onChange={e => setCol("t151_300", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.tmas_300 || ""} onChange={e => setCol("tmas_300", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th></th>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <th className="text-left p-2.5">Clasif.</th>
                    <th className="text-left p-2.5">Actividad</th>
                    <th className="text-right p-2.5">≤50</th>
                    <th className="text-right p-2.5">51-100</th>
                    <th className="text-right p-2.5">101-400</th>
                    <th className="text-right p-2.5">401-700</th>
                    <th className="text-right p-2.5">701-1000</th>
                    <th className="text-right p-2.5">&gt;1000</th>
                    <th className="text-right p-2.5">
                      {hasFilters && <Button size="sm" variant="ghost" onClick={clearFilters} className="text-xs h-7 px-2 text-red-600 hover:text-red-700" data-testid="btn-clear-filters">Limpiar</Button>}
                    </th>
                  </tr>
                  <tr className="bg-white">
                    <th className="p-1.5"><Input data-testid="colfilter-clasif" value={colFilters.clasif || ""} onChange={e => setCol("clasif", e.target.value)} placeholder="13.1…" className="h-7 text-xs font-mono" /></th>
                    <th className="p-1.5"><Input data-testid="colfilter-actesp" value={colFilters.actesp || ""} onChange={e => setCol("actesp", e.target.value)} placeholder="Filtrar actividad…" className="h-7 text-xs" /></th>
                    <th className="p-1.5"><Input value={colFilters.t50 || ""} onChange={e => setCol("t50", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.t51_100 || ""} onChange={e => setCol("t51_100", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.t101_400 || ""} onChange={e => setCol("t101_400", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.t401_700 || ""} onChange={e => setCol("t401_700", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.t701_1000 || ""} onChange={e => setCol("t701_1000", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th className="p-1.5"><Input value={colFilters.tmas_1000 || ""} onChange={e => setCol("tmas_1000", e.target.value)} placeholder="Bs." className="h-7 text-xs text-right font-mono" /></th>
                    <th></th>
                  </tr>
                </>
              )}
            </thead>
            <tbody>
              {tarifasFiltradas.map(t => tipoTab === "no-industrial" ? (
                <tr key={t.id} className="border-b border-green-50 hover:bg-green-50/40">
                  <td className="p-2.5"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color_clasificacion }} /><span className="font-mono font-bold">{t.codigo_clasificacion}</span></span></td>
                  <td className="p-2.5">{t.actividad_economica}</td>
                  <td className="p-2.5 font-medium">{t.actividad_especifica}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_hasta_150 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_151_300 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_mas_300 ?? "—"}</td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => edit(t)}><Edit className="w-4 h-4 text-blue-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(t)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              ) : (
                <tr key={t.id} className="border-b border-green-50 hover:bg-green-50/40">
                  <td className="p-2.5"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color_clasificacion }} /><span className="font-mono font-bold">{t.codigo_clasificacion}</span></span></td>
                  <td className="p-2.5 font-medium">{t.actividad_especifica}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_hasta_50 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_51_100 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_101_400 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_401_700 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_701_1000 ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono">{t.tarifa_mas_1000 ?? "—"}</td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => edit(t)}><Edit className="w-4 h-4 text-blue-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(t)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {!tarifasFiltradas.length && <tr><td colSpan={tipoTab === "no-industrial" ? 7 : 9} className="p-8 text-center text-slate-400">Sin tarifas {tipoTab}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Rutas({ auth, API }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = { codigo:"", nombre:"", descripcion:"" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await axios.get(`${API}/aseo-urbano/rutas`, auth);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const closeDialog = () => { setOpen(false); setEditingId(null); setForm(empty); };
  const save = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/aseo-urbano/rutas/${editingId}`, form, auth);
        toast.success("Ruta actualizada");
      } else {
        await axios.post(`${API}/aseo-urbano/rutas`, form, auth);
        toast.success("Ruta creada");
      }
      closeDialog(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (r) => {
    setEditingId(r.id);
    setForm({ codigo: r.codigo || "", nombre: r.nombre || "", descripcion: r.descripcion || "" });
    setOpen(true);
  };
  const del = async (r) => { if (!window.confirm(`¿Eliminar la ruta ${r.codigo}?`)) return; await axios.delete(`${API}/aseo-urbano/rutas/${r.id}`, auth); toast.success("Ruta eliminada"); load(); };
  const toggleActive = async (r) => {
    await axios.put(`${API}/aseo-urbano/rutas/${r.id}`, { is_active: !r.is_active }, auth);
    toast.success(r.is_active ? "Ruta desactivada" : "Ruta activada");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) closeDialog(); }}>
          <DialogTrigger asChild><Button data-testid="btn-add-ruta" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nueva Ruta</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Editar ruta" : "Nueva ruta"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Código*</Label><Input data-testid="input-ruta-codigo" value={form.codigo} onChange={e => setForm({...form, codigo:e.target.value})} placeholder="Ej. R-01" /></div>
              <div><Label>Nombre*</Label><Input data-testid="input-ruta-nombre" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} placeholder="Ej. Centro Norte" /></div>
              <div><Label>Descripción</Label><Input data-testid="input-ruta-descripcion" value={form.descripcion} onChange={e => setForm({...form, descripcion:e.target.value})} placeholder="Detalle adicional" /></div>
            </div>
            <DialogFooter><Button data-testid="btn-save-ruta" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editingId ? "Actualizar" : "Crear"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-green-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              <tr>
                <th className="text-left p-2.5">Código</th>
                <th className="text-left p-2.5">Nombre</th>
                <th className="text-left p-2.5">Descripción</th>
                <th className="text-center p-2.5">Estatus</th>
                <th className="text-right p-2.5">Acciones</th>
              </tr>
            </thead>
            <tbody>{items.map(r => (
              <tr key={r.id} className="border-b border-green-50 hover:bg-green-50/40" data-testid={`row-ruta-${r.codigo}`}>
                <td className="p-2.5 font-mono font-bold text-green-900">{r.codigo}</td>
                <td className="p-2.5 font-medium">{r.nombre}</td>
                <td className="p-2.5 text-slate-600">{r.descripcion || "-"}</td>
                <td className="p-2.5 text-center">{r.is_active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Activa</Badge> : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Inactiva</Badge>}</td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => edit(r)} title="Editar" data-testid={`btn-edit-ruta-${r.codigo}`}><Edit className="w-4 h-4 text-blue-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => toggleActive(r)} title={r.is_active ? "Desactivar" : "Activar"} className="px-2 text-xs font-semibold">{r.is_active ? "⏸" : "▶"}</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(r)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}{!items.length && <tr><td colSpan="5" className="p-8 text-center text-slate-400">Sin rutas registradas</td></tr>}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
